class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector("details");
    this.content = this.mainDetailsToggle.querySelector("summary").nextElementSibling;
    // 新增：hover 延迟关闭定时器（避免快速划过触发）
    this.hoverTimer = null;
    // 移除原有的 focusout/toggle 事件，绑定 hover 事件
    this.bindHoverEvents();
  }

  // 新增：绑定 hover 事件（核心改造点）
  bindHoverEvents() {
    // 鼠标进入组件 → 展开面板
    this.addEventListener("mouseenter", this.onMouseEnter.bind(this));
    // 鼠标离开组件 → 延迟关闭面板
    this.addEventListener("mouseleave", this.onMouseLeave.bind(this));
    // 给 details 本身也绑定 hover（兼容子元素hover）
    this.mainDetailsToggle.addEventListener("mouseenter", this.onMouseEnter.bind(this));
    this.mainDetailsToggle.addEventListener("mouseleave", this.onMouseLeave.bind(this));
  }

  // 鼠标进入 → 展开面板
  onMouseEnter() {
    // 清除延迟关闭定时器
    clearTimeout(this.hoverTimer);
    // 标记为展开状态
    this.mainDetailsToggle.setAttribute("open", "true");
    // 更新无障碍属性
    this.mainDetailsToggle.querySelector("summary").setAttribute("aria-expanded", true);
    // 播放动画（复用原有逻辑）
    this.handleAnimation(true);
    // 触发自定义 toggle 逻辑（供子类扩展）
    this.onToggle();
  }

  // 鼠标离开 → 延迟关闭面板（优化体验）
  onMouseLeave() {
    // 300ms 延迟关闭，避免快速划过触发
    this.hoverTimer = setTimeout(() => {
      this.close();
      // 取消动画（复用原有逻辑）
      this.handleAnimation(false);
      // 触发自定义 toggle 逻辑
      this.onToggle();
    }, 300);
  }

  // 重构：统一处理动画播放/取消
  handleAnimation(isOpen) {
    if (!this.animations) this.animations = this.content.getAnimations();
    if (isOpen) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  // 移除原有 onFocusOut（hover 逻辑替代）
  // 重构 onToggle：从事件触发改为手动调用
  onToggle() {
    // 空方法，供子类重写
  }

  // 保留原有 close 方法（更新无障碍属性）
  close() {
    this.mainDetailsToggle.removeAttribute("open");
    this.mainDetailsToggle.querySelector("summary").setAttribute("aria-expanded", false);
  }

  // 组件销毁时清除定时器（避免内存泄漏）
  disconnectedCallback() {
    clearTimeout(this.hoverTimer);
  }
}

customElements.define("details-disclosure", DetailsDisclosure);

class HeaderMenu extends HTMLElement {
  constructor() {
    super();
    this.header = document.querySelector(".header-wrapper");
    this.mainDetailsToggle = this.querySelector("details");
    this.nav = this.closest(".header__inline-menu");
    this.mainDetailsToggle.addEventListener("toggle", this.onToggle.bind(this));

    if (this.nav && this.nav.classList.contains("hover-mode-all")) {
      this.initAllHover();
    } else {
      this.bindHoverEvents();
    }
  }

  onToggle() {
    if (!this.header) return;
    // 根据面板是否展开设置 preventHide 状态
    this.header.preventHide = this.mainDetailsToggle.open;

    // 仅初始化一次 CSS 变量
    if (document.documentElement.style.getPropertyValue("--header-bottom-position-desktop") !== "")
      return;
    document.documentElement.style.setProperty(
      "--header-bottom-position-desktop",
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`,
    );
  }

  close() {
    this.mainDetailsToggle.removeAttribute("open");
    this.mainDetailsToggle.querySelector("summary").setAttribute("aria-expanded", false);
  }

  disconnectedCallback() {
    clearTimeout(this.hoverTimer);
    clearTimeout(this.closeTimer);
  }

  // mega 菜单 / dropdown single 模式：悬停单项展开自己，移开延迟收起。
  // 面板是 details 的子元素，鼠标从 summary 穿过间隙进入面板时 mouseenter 会清掉收起定时器，不会中断
  bindHoverEvents() {
    const details = this.mainDetailsToggle;
    const summary = details.querySelector("summary");
    const open = () => {
      clearTimeout(this.hoverTimer);
      details.setAttribute("open", "");
      summary.setAttribute("aria-expanded", "true");
    };
    details.addEventListener("mouseenter", open);
    details.addEventListener("mouseleave", () => {
      clearTimeout(this.hoverTimer);
      this.hoverTimer = setTimeout(() => this.close(), 300);
    });
  }

  // dropdown all 模式：悬停任意主菜单项 → 所有 details 同步置 open + aria-expanded=true，
  // 并给 nav 加 menu-open 类显示共享汇总大面板（submenu-all-panel）；
  // 离开导航整体（含汇总面板，它是 nav 后代 hover 链不断）→ 延时统一收起。
  // 仅初始化一次（每个菜单项一个实例，绑定挂在 nav 级别共享）
  initAllHover() {
    if (this.nav.dataset.allHoverInit) return;
    this.nav.dataset.allHoverInit = "true";
    const detailsList = Array.from(this.nav.querySelectorAll("header-menu > details"));

    // 按主菜单项位置对齐汇总面板内各分组：分组左缘对齐菜单项文字左缘，再左移子菜单左内边距
    //（nav 的 data-submenu-pad-left，rem 值）——子项 padding-left 会把文字推右等量距离，
    // 两相抵消后文字仍与主菜单对齐；列宽超过菜单项间距时右推至最小间隙（3.2rem）防重叠。
    // 纯 CSS 无法让面板内元素对齐到外部菜单项位置，此项必须 JS 计算
    const alignGroups = () => {
      const panel = this.nav.querySelector(":scope > .submenu-all-panel");
      if (!panel) return;
      const panelLeft = panel.getBoundingClientRect().left;
      const groups = Array.from(panel.querySelectorAll(":scope > .submenu-group"));
      const summaries = this.nav.querySelectorAll(":scope > ul > li > header-menu > details > summary");
      const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 10;
      const padLeft =
        (parseFloat(this.nav.dataset.submenuPadLeft) || 0) * rootFontSize;
      let cursor = 0; /* 前面分组占用的宽度累计（含 margin） */
      summaries.forEach((summary, index) => {
        const group = groups[index];
        if (!group) return;
        const itemLeft = summary.getBoundingClientRect().left - panelLeft - padLeft;
        const margin = Math.max(itemLeft - cursor, 32);
        group.style.marginLeft = `${margin}px`;
        cursor += margin + group.offsetWidth;
      });
    };

    const openAll = () => {
      alignGroups(); /* 展开前重算，保证按当前菜单项位置对齐 */
      this.nav.classList.add("menu-open");
      detailsList.forEach((details) => {
        details.setAttribute("open", "");
        details.querySelector("summary").setAttribute("aria-expanded", "true");
      });
    };
    const closeAll = () => {
      this.nav.classList.remove("menu-open");
      detailsList.forEach((details) => {
        details.removeAttribute("open");
        details.querySelector("summary").setAttribute("aria-expanded", false);
      });
    };

    // 字体加载完成后宽度会变化，重算一次；窗口尺寸变化时防抖重算
    if (document.fonts) document.fonts.ready.then(() => alignGroups());
    window.addEventListener("resize", () => {
      clearTimeout(this.alignTimer);
      this.alignTimer = setTimeout(alignGroups, 200);
    });
    alignGroups();

    // 悬停有子菜单的主菜单项（li 下含 header-menu）才触发展开；无子菜单的纯链接项不展开。
    // 面板是各自 li 的子孙，悬停面板时所属 li 同样命中，不会中断
    this.nav.querySelectorAll(":scope > ul > li:has(> header-menu)").forEach((li) => {
      li.addEventListener("mouseenter", openAll);
    });

    // 离开导航整体：延时收起，防划过间隙误收；收起前复查鼠标已移回则不收
    this.nav.addEventListener("mouseleave", () => {
      clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(() => {
        if (this.nav.matches(":hover")) return;
        closeAll();
      }, 300);
    });
    this.nav.addEventListener("mouseenter", () => clearTimeout(this.closeTimer));

    // 点击菜单项：悬停模式下已展开，阻止原生 toggle 单项切换打破全组同步状态
    detailsList.forEach((details) => {
      details.querySelector("summary").addEventListener("click", (event) => event.preventDefault());
    });
  }
}

customElements.define("header-menu", HeaderMenu);
